import * as d3 from 'd3'

import type { ChartScales } from '@/features/bidding/types/bidLadder'
import {
  AXIS_LABEL_FONT_SIZE,
  AXIS_LABEL_FONT_WEIGHT,
  AXIS_LABEL_LETTER_SPACING,
  AXIS_LINE_COLOR,
  AXIS_STROKE_WIDTH,
  AXIS_TEXT_COLOR_PRIMARY,
  AXIS_TEXT_COLOR_SECONDARY,
  AXIS_TICK_PADDING,
  X_AXIS_LABEL_OFFSET,
  Y_AXIS_LABEL_OFFSET,
} from '@/features/bidding/utils/cascading/chartConstants'

// Calculate optimal number of x-axis ticks based on chart width
function getXTickCount(chartWidth: number): number {
  // Aim for approximately one tick per 60-80 pixels, with min/max bounds
  const ticksPerPixel = chartWidth / 70
  return Math.max(4, Math.min(Math.round(ticksPerPixel), 16))
}

export function renderAxes(g: d3.Selection<SVGGElement, unknown, null, undefined>, scales: ChartScales) {
  const { xScale, yScale, chartWidth, chartHeight } = scales
  const tickGroup = g.append('g').attr('class', 'tick-lines')

  // Dynamic x-axis tick count based on chart width
  const numberXTicks = getXTickCount(chartWidth)

  // Horizontal tick lines (left side only)
  const yTicks = yScale.ticks(6)

  // Left side ticks
  tickGroup
    .selectAll('.tick-line-y-left')
    .data(yTicks)
    .enter()
    .append('line')
    .attr('class', 'tick-line-y-left')
    .attr('x1', -6)
    .attr('x2', 0)
    .attr('y1', (d) => yScale(d))
    .attr('y2', (d) => yScale(d))
    .attr('stroke', AXIS_LINE_COLOR)
    .attr('stroke-width', AXIS_STROKE_WIDTH)

  // Removed right side ticks for cleaner single-axis design

  // Vertical tick lines (from x-axis)
  const xTicks = xScale.ticks(numberXTicks)
  tickGroup
    .selectAll('.tick-line-x')
    .data(xTicks)
    .enter()
    .append('line')
    .attr('class', 'tick-line-x')
    .attr('x1', (d) => xScale(d))
    .attr('x2', (d) => xScale(d))
    .attr('y1', chartHeight)
    .attr('y2', chartHeight + 6)
    .attr('stroke', '#d1d5db')
    .attr('stroke-width', AXIS_STROKE_WIDTH)

  // X-axis (centered at 0)
  const xAxis = d3
    .axisBottom(xScale)
    .ticks(numberXTicks)
    .tickFormat((d) => `${Math.abs(Number(d))}`) // Show absolute values
    .tickSize(0)
    .tickPadding(AXIS_TICK_PADDING)

  // Y-axis (left side only)
  const yAxisLeft = d3
    .axisLeft(yScale)
    .ticks(6)
    .tickFormat((d) => `€${d}`)
    .tickSize(0)
    .tickPadding(AXIS_TICK_PADDING)

  // Removed right y-axis for cleaner single-axis design

  // X-axis
  const xAxisGroup = g.append('g').attr('class', 'x-axis').attr('transform', `translate(0,${chartHeight})`).call(xAxis)

  xAxisGroup
    .selectAll('text')
    .style('font-size', AXIS_LABEL_FONT_SIZE)
    .style('font-weight', AXIS_LABEL_FONT_WEIGHT)
    .style('fill', AXIS_TEXT_COLOR_SECONDARY)

  xAxisGroup.select('.domain').attr('stroke', AXIS_LINE_COLOR).attr('stroke-width', AXIS_STROKE_WIDTH)

  // X-axis label
  g.append('text')
    .attr('x', chartWidth / 2)
    .attr('y', chartHeight + X_AXIS_LABEL_OFFSET)
    .attr('text-anchor', 'middle')
    .attr('font-size', AXIS_LABEL_FONT_SIZE)
    .attr('font-weight', AXIS_LABEL_FONT_WEIGHT)
    .attr('fill', AXIS_TEXT_COLOR_PRIMARY)
    .style('letter-spacing', AXIS_LABEL_LETTER_SPACING)
    .text('CUMULATIVE CAPACITY (MW)')

  // Left Y-axis (single axis)
  const yAxisGroupLeft = g.append('g').attr('class', 'y-axis-left').call(yAxisLeft)

  yAxisGroupLeft
    .selectAll('text')
    .style('font-size', AXIS_LABEL_FONT_SIZE)
    .style('font-weight', AXIS_LABEL_FONT_WEIGHT)
    .style('fill', AXIS_TEXT_COLOR_PRIMARY)

  yAxisGroupLeft.select('.domain').attr('stroke', AXIS_LINE_COLOR).attr('stroke-width', AXIS_STROKE_WIDTH)

  // Removed right y-axis for cleaner design

  // Y-axis label (left side)
  g.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('x', -chartHeight / 2)
    .attr('y', Y_AXIS_LABEL_OFFSET)
    .attr('text-anchor', 'middle')
    .attr('font-size', AXIS_LABEL_FONT_SIZE)
    .attr('font-weight', AXIS_LABEL_FONT_WEIGHT)
    .attr('fill', AXIS_TEXT_COLOR_PRIMARY)
    .style('letter-spacing', AXIS_LABEL_LETTER_SPACING)
    .text('PRICE (€/MWh)')
}
